import { useState } from 'react'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  FileText,
  Loader2,
  Eye,
  BarChart2,
  Search,
} from 'lucide-react'
import { useClients } from '@/hooks/queries/useClients'
import {
  useEsgReports,
  useEsgPreview,
  useCreateEsgReport,
  useDownloadEsgReport,
} from '@/hooks/queries/useEsgReports'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Combobox } from '@/components/ui/combobox'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import type {
  GovernanceData,
  GovernanceLevel,
  GovernanceStatus,
  EsgReportFormData,
} from '@/types'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function openBlobInNewTab(blob: Blob): void {
  const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
  const url = URL.createObjectURL(pdfBlob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

// ─── Esquema de validación ────────────────────────────────────────────────────

const governanceAspectSchema = z.object({
  status: z.enum(['implemented', 'in_progress', 'not_implemented']),
  level: z.enum(['high', 'medium', 'low']),
})

const esgReportSchema = z.object({
  client_id: z.number().refine((v) => v > 0, 'Debes seleccionar un cliente'),
  date_from: z.string().min(1, 'La fecha de inicio es obligatoria'),
  date_to: z.string().min(1, 'La fecha de fin es obligatoria'),
  discarded_kg: z.number().min(0, 'El descarte no puede ser negativo'),
  collaborators: z.number().min(0, 'Debe ser 0 o más'),
  hours_generated: z.number().min(0, 'Las horas no pueden ser negativas'),
  women_participation_pct: z.number().min(0, 'Mínimo 0').max(100, 'Máximo 100'),
  governance: z.object({
    traceability: governanceAspectSchema,
    digital_record: governanceAspectSchema,
    esg_reporting: governanceAspectSchema,
    photo_record: governanceAspectSchema,
  }),
  governance_insight: z.string(),
  conclusions: z.string(),
}).refine(
  (d) => !d.date_from || !d.date_to || d.date_to >= d.date_from,
  { message: 'La fecha de fin debe ser igual o posterior al inicio', path: ['date_to'] }
)

// ─────────────────────────────────────────────────────────────────────────────

const defaultGovernance: GovernanceData = {
  traceability: { status: 'implemented', level: 'high' },
  digital_record: { status: 'implemented', level: 'high' },
  esg_reporting: { status: 'implemented', level: 'high' },
  photo_record: { status: 'implemented', level: 'high' },
}

const governanceLabels: Record<keyof GovernanceData, string> = {
  traceability: 'Trazabilidad de residuos',
  digital_record: 'Registro digital',
  esg_reporting: 'Reportabilidad ESG',
  photo_record: 'Registro fotográfico',
}

const statusOptions: { value: GovernanceStatus; label: string }[] = [
  { value: 'implemented', label: 'Implementado' },
  { value: 'in_progress', label: 'En implementación' },
  { value: 'not_implemented', label: 'No implementado' },
]

const levelOptions: { value: GovernanceLevel; label: string }[] = [
  { value: 'high', label: 'Alto' },
  { value: 'medium', label: 'Medio' },
  { value: 'low', label: 'Bajo' },
]

// ─── Componente GovernanceRow ─────────────────────────────────────────────────

interface GovernanceRowProps {
  aspectKey: keyof GovernanceData
  label: string
  governance: GovernanceData
  onChange: (key: keyof GovernanceData, field: 'status' | 'level', value: string) => void
}

function GovernanceRow({ aspectKey, label, governance, onChange }: GovernanceRowProps) {
  const aspect = governance[aspectKey]
  return (
    <div className="grid grid-cols-3 gap-3 items-center py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Select
        value={aspect.status}
        onValueChange={(v) => onChange(aspectKey, 'status', v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={aspect.level}
        onValueChange={(v) => onChange(aspectKey, 'level', v)}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {levelOptions.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function EsgReportPage() {
  const { data: clients, isLoading: loadingClients } = useClients()

  // Sección A: selección
  const [clientId, setClientId] = useState<string>('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [previewParams, setPreviewParams] = useState<{
    client_id: number
    date_from: string
    date_to: string
  } | null>(null)

  // Sección B: inputs manuales
  const [discardedKg, setDiscardedKg] = useState('')
  const [collaborators, setCollaborators] = useState('')
  const [hoursGenerated, setHoursGenerated] = useState('')
  const [womenPct, setWomenPct] = useState('')
  const [governance, setGovernance] = useState<GovernanceData>(defaultGovernance)
  const [governanceInsight, setGovernanceInsight] = useState('')
  const [conclusions, setConclusions] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const { data: metrics, isLoading: loadingPreview, error: previewError } = useEsgPreview(previewParams)
  const createEsgReport = useCreateEsgReport()
  const downloadReport = useDownloadEsgReport()

  // Historial: filtrado por cliente seleccionado
  const { data: reports, isLoading: loadingReports } = useEsgReports(
    clientId ? { client_id: parseInt(clientId) } : undefined
  )

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleLoadPreview() {
    if (!clientId || !dateFrom || !dateTo) {
      toast.error('Selecciona cliente y período antes de cargar los datos.')
      return
    }
    setPreviewParams({
      client_id: parseInt(clientId),
      date_from: dateFrom,
      date_to: dateTo,
    })
  }

  function handleGovernanceChange(
    key: keyof GovernanceData,
    field: 'status' | 'level',
    value: string
  ) {
    setGovernance((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }))
  }

  async function handleGenerate() {
    setFieldErrors({})

    const payload: EsgReportFormData = {
      client_id: parseInt(clientId) || 0,
      date_from: dateFrom,
      date_to: dateTo,
      discarded_kg: parseFloat(discardedKg) || 0,
      collaborators: parseInt(collaborators) || 0,
      hours_generated: parseFloat(hoursGenerated) || 0,
      women_participation_pct: parseFloat(womenPct) || 0,
      governance,
      governance_insight: governanceInsight,
      conclusions,
    }

    const mapped: Record<string, string> = {}

    const result = esgReportSchema.safeParse(payload)
    if (!result.success) {
      for (const issue of result.error.issues) {
        const field = String(issue.path[0] ?? '')
        if (field && !mapped[field]) mapped[field] = issue.message
      }
    }

    if (!collaborators.trim()) mapped.collaborators ??= 'El número de colaboradoras es obligatorio'
    if (!hoursGenerated.trim()) mapped.hours_generated ??= 'Las horas de trabajo son obligatorias'
    if (!womenPct.trim()) mapped.women_participation_pct ??= 'El porcentaje de participación es obligatorio'

    if (metrics && payload.discarded_kg > metrics.total_kg) {
      mapped.discarded_kg ??= `El descarte no puede superar el total gestionado (${metrics.total_kg.toLocaleString('es-CL', { maximumFractionDigits: 0 })} kg)`
    }

    if (Object.keys(mapped).length > 0) {
      setFieldErrors(mapped)
      toast.error('Corrige los errores del formulario.')
      return
    }

    createEsgReport.mutate(payload, {
      onSuccess: (blob) => {
        openBlobInNewTab(blob)
        toast.success('Informe generado correctamente.')
      },
      onError: (err) => {
        const anyErr = err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
        const message = anyErr?.response?.data?.message ?? 'Error al generar el informe.'
        const errors = anyErr?.response?.data?.errors ?? {}
        const flat: Record<string, string> = {}
        for (const [k, msgs] of Object.entries(errors)) {
          flat[k] = msgs[0]
        }
        setFieldErrors(flat)
        toast.error(message)
      },
    })
  }

  async function handleDownload(id: number) {
    downloadReport.mutate(id, {
      onSuccess: (blob) => {
        openBlobInNewTab(blob)
      },
      onError: () => {
        toast.error('No se pudo abrir el informe.')
      },
    })
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <FileText className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Informes ESG</h1>
          <p className="text-sm text-muted-foreground">
            Genera informes de sostenibilidad ambiental, social y de gobernanza por cliente y período.
          </p>
        </div>
      </div>

      {/* ── Sección A: Selección de cliente y período ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Selección de cliente y período</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Combobox
                options={clients?.map((c) => ({ value: String(c.id), label: c.trade_name })) ?? []}
                value={clientId || null}
                onChange={(v) => setClientId(v)}
                placeholder="Selecciona un cliente…"
                disabled={loadingClients}
              />
              {fieldErrors['client_id'] && (
                <p className="text-xs text-destructive">{fieldErrors['client_id']}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              {fieldErrors['date_from'] && (
                <p className="text-xs text-destructive">{fieldErrors['date_from']}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
              {fieldErrors['date_to'] && (
                <p className="text-xs text-destructive">{fieldErrors['date_to']}</p>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={handleLoadPreview} disabled={loadingPreview}>
            {loadingPreview ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            Ver datos del período
          </Button>
        </CardContent>
      </Card>

      {/* ── Sección B: Preview + Inputs manuales ── */}
      {previewError && (
        <Card className="border-destructive">
          <CardContent className="pt-4 text-sm text-destructive">
            No se pudieron cargar los datos del período seleccionado.
          </CardContent>
        </Card>
      )}

      {metrics && (
        <>
          {/* Preview de datos calculados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart2 className="h-4 w-4" />
                2. Datos calculados del período
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {metrics.total_kg.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Total kg gestionados</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {metrics.valorized_kg.toLocaleString('es-CL', { maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">kg valorizados</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{metrics.valorization_pct}%</p>
                  <p className="text-xs text-muted-foreground mt-1">% valorización</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-slate-700">
                    {metrics.co2e_avoided_tons.toLocaleString('es-CL', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">t CO2e evitadas</p>
                </div>
              </div>

                <div className="rounded-lg border bg-slate-50/70 px-4 py-3 text-sm text-slate-700">
                  <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                    <span>
                      CO2e evitado: {metrics.co2e_avoided_kg.toLocaleString('es-CL', {
                      maximumFractionDigits: 2,
                    })} kg
                    </span>
                    <span>
                    Cobertura de cálculo: {metrics.co2e_coverage_pct === null
                      ? '—'
                      : `${metrics.co2e_coverage_pct.toLocaleString('es-CL', {
                        maximumFractionDigits: 2,
                      })}%`}
                    </span>
                  </div>
                {metrics.co2e_pending_types.length > 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    Tipos sin factor en el período: {metrics.co2e_pending_types.map((item) => item.name).join(', ')}
                  </p>
                )}
              </div>

              {metrics.by_category.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Por categoría</p>
                  <div className="space-y-1">
                    {metrics.by_category.map((c) => (
                      <div key={c.name} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{c.name}</span>
                        <span className="font-medium">
                          {c.kg.toLocaleString('es-CL', { maximumFractionDigits: 1 })} kg
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inputs manuales */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">3. Datos de desempeño manual</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Kg descartados */}
              <div className="space-y-1.5">
                <Label>Kilos descartados *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={discardedKg}
                  onChange={(e) => setDiscardedKg(e.target.value)}
                  placeholder="0.00"
                />
                {fieldErrors['discarded_kg'] && (
                  <p className="text-xs text-destructive">{fieldErrors['discarded_kg']}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Máximo: {metrics.total_kg.toLocaleString('es-CL', { maximumFractionDigits: 0 })} kg
                </p>
              </div>

              {/* Desempeño social */}
              <div>
                <p className="text-sm font-medium mb-3">Desempeño social</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label>Número de colaboradoras *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={collaborators}
                      onChange={(e) => setCollaborators(e.target.value)}
                      placeholder="0"
                    />
                    {fieldErrors['collaborators'] && (
                      <p className="text-xs text-destructive">{fieldErrors['collaborators']}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Horas de trabajo generadas *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={hoursGenerated}
                      onChange={(e) => setHoursGenerated(e.target.value)}
                      placeholder="0.0"
                    />
                    {fieldErrors['hours_generated'] && (
                      <p className="text-xs text-destructive">{fieldErrors['hours_generated']}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>% participación de mujeres *</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={womenPct}
                      onChange={(e) => setWomenPct(e.target.value)}
                      placeholder="0.0"
                    />
                    {fieldErrors['women_participation_pct'] && (
                      <p className="text-xs text-destructive">
                        {fieldErrors['women_participation_pct']}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Gobernanza */}
              <div>
                <p className="text-sm font-medium mb-2">Gobernanza</p>
                <div className="grid grid-cols-3 gap-3 mb-1">
                  <span className="text-xs font-medium text-muted-foreground">Aspecto</span>
                  <span className="text-xs font-medium text-muted-foreground">Estado</span>
                  <span className="text-xs font-medium text-muted-foreground">Nivel</span>
                </div>
                {(Object.keys(governanceLabels) as (keyof GovernanceData)[]).map((key) => (
                  <GovernanceRow
                    key={key}
                    aspectKey={key}
                    label={governanceLabels[key]}
                    governance={governance}
                    onChange={handleGovernanceChange}
                  />
                ))}
              </div>

              {/* Insight de gobernanza */}
              <div className="space-y-1.5">
                <Label>Insight de gobernanza</Label>
                <Textarea
                  value={governanceInsight}
                  onChange={(e) => setGovernanceInsight(e.target.value)}
                  placeholder="Comentarios sobre el estado de gobernanza del cliente…"
                  rows={3}
                />
              </div>

              {/* Conclusiones */}
              <div className="space-y-1.5">
                <Label>Conclusiones y recomendaciones</Label>
                <Textarea
                  value={conclusions}
                  onChange={(e) => setConclusions(e.target.value)}
                  placeholder="Conclusiones del período y recomendaciones para el cliente…"
                  rows={4}
                />
              </div>

              {/* Botón generar */}
              <Button
                onClick={handleGenerate}
                disabled={createEsgReport.isPending}
                className="w-full"
              >
                {createEsgReport.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generando informe…
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    Generar informe ESG
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── Historial de informes ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de informes</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingReports ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : !reports || reports.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              {clientId
                ? 'Este cliente no tiene informes generados.'
                : 'Selecciona un cliente para ver su historial de informes.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Cliente</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Desde</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Hasta</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Generado</th>
                    <th className="py-2 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 px-2">{report.client?.trade_name ?? '—'}</td>
                      <td className="py-2 px-2">
                        {new Date(report.from).toLocaleDateString('es-CL')}
                      </td>
                      <td className="py-2 px-2">
                        {new Date(report.to).toLocaleDateString('es-CL')}
                      </td>
                      <td className="py-2 px-2">
                        {new Date(report.generated_at).toLocaleDateString('es-CL', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={downloadReport.isPending}
                          onClick={() => handleDownload(report.id)}
                        >
                          {downloadReport.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Eye className="h-3 w-3 mr-1" />
                          )}
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
